import { useState, useEffect, useMemo } from "react";
import Buttons from "../../../components/ui/button/Buttons";
import { useAddCRUDCategoryMutation, useEditCRUDCategoryMutation, useLazyDeleteCRUDCategoryByIdQuery, useLazyGetAllCRUDCategoryListQuery } from "../../../app/services/Crud/crudApi";
import SwalAlert from "../../../services/swalService/SwalService";
import Select from "react-select";
import DataLoader from "../../../components/ui/dataLoader/DataLoader";
import NoRecordFound from "../../../components/ui/noRecordFound/NoRecordFound";
import "./CrudCategory.scss"
const PAGE_SIZE = 10;

const categoryOptions = [
  { label: "Product", value: "Product" },
  { label: "Service", value: "Service" }
];
const subCategoryOptions = [
  { label: "L1", value: "L1" },
  { label: "L2", value: "L2" },
  { label: "L3", value: "L3" },
  { label: "L1-ZCP", value: "L1-ZCP" }
];

const serviceSubCategoryOptions = subCategoryOptions.filter(
  (opt) => opt.value === "L1" || opt.value === "L1-ZCP"
);
const productSubCategoryOptions = subCategoryOptions.filter(
  (opt) => opt.value !== "L1-ZCP"
);

const categoryMap = { Product: 1, Service: 2 };
const subCategoryMap = { L1: 1, L2: 2, L3: 3, "L1-ZCP": 4 };

const EMPTY_FILTER = { channel: null, description: "" };

// makes the filter dropdown the same height as the text input
const filterSelectStyles = {
  control: (base) => ({ ...base, minHeight: 38, height: 38 }),
  valueContainer: (base) => ({ ...base, height: 38, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 38 }),
};

const CRUDCategoryList = () => {
  const [dataSource, setDataSource] = useState([]);
  const { error, success, confirm } = SwalAlert();
  const [isAdding, setIsAdding] = useState(false);

  // filter + pagination state
  const [filterInput, setFilterInput] = useState(EMPTY_FILTER);     // what the user is typing
  const [appliedFilter, setAppliedFilter] = useState(EMPTY_FILTER); // applied on Search click
  const [pageNo, setPageNo] = useState(1);

  const [getAllCRUDCategoryList, { data: crudCategoryListData, isLoading: isCrudCategoryLoading }] = useLazyGetAllCRUDCategoryListQuery();
  const [addCRUDCategory, { data: addCRUDCategoryData, isLoading: addCRUDCategoryLoading }] = useAddCRUDCategoryMutation();
  const [editCRUDCategory, { data: editCRUDCategoryData, isLoading: editCRUDCategoryLoading }] = useEditCRUDCategoryMutation();
  const [deleteCRUDCategoryById, { isLoading: isLoadingdeleteCRUDCategoryById, data: deleteCRUDCategoryByIdResponse }] = useLazyDeleteCRUDCategoryByIdQuery();

  useEffect(() => {
    getAllCRUDCategoryList();
  }, []);

  useEffect(() => {
    if (!addCRUDCategoryLoading && addCRUDCategoryData) {
      if (addCRUDCategoryData.hasError) {
        error(addCRUDCategoryData.errorMessage);
        setDataSource((prev) => prev.filter((item) => !item.isNew));
      } else {
        success("CRUD Category added successfully.");
      }
      getAllCRUDCategoryList();
      setIsAdding(false);
    }
  }, [addCRUDCategoryLoading, addCRUDCategoryData]);

  useEffect(() => {
    if (!editCRUDCategoryLoading && editCRUDCategoryData) {
      if (editCRUDCategoryData.hasError) {
        error(editCRUDCategoryData.errorMessage);
        setDataSource((prev) =>
          prev.map((item) =>
            item.id === editCRUDCategoryData.keyId ? { ...item.originalRow, isEditing: false } : item
          )
        );
      } else {
        success("CRUD Category updated successfully.");
      }
      getAllCRUDCategoryList();
    }
  }, [editCRUDCategoryLoading, editCRUDCategoryData]);

  useEffect(() => {
    if (!isLoadingdeleteCRUDCategoryById && deleteCRUDCategoryByIdResponse) {
      success("CRUD Category deleted successfully.");
      getAllCRUDCategoryList();
    }
  }, [isLoadingdeleteCRUDCategoryById, deleteCRUDCategoryByIdResponse]);

  useEffect(() => {
    if (crudCategoryListData) {
      const formattedData = crudCategoryListData.map((item) => ({
        id: item.id,
        category: item.category,
        subCategory: item.category === "Service" ? (item.isZCP ? "L1-ZCP" : "L1") : item.subCategory,
        description: item.description,
        isEditing: false,
        isNew: false
      }));
      setDataSource(formattedData);
    }
  }, [crudCategoryListData]);

  /* ---------------- filtering + pagination ---------------- */

  const filteredData = useMemo(() => {
    const desc = appliedFilter.description.trim().toLowerCase();
    const channel = appliedFilter.channel;

    return dataSource.filter((row) => {
      // never hide rows the user is currently adding/editing
      if (row.isNew || row.isEditing) return true;

      const matchChannel = !channel || row.category === channel;
      const matchDesc = !desc || (row.description || "").toLowerCase().includes(desc);
      return matchChannel && matchDesc;
    });
  }, [dataSource, appliedFilter]);

  const totalCount = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const pagedData = useMemo(() => {
    const start = (pageNo - 1) * PAGE_SIZE;
    return filteredData.slice(start, start + PAGE_SIZE);
  }, [filteredData, pageNo]);

  // keep page valid after delete / filter / reload
  useEffect(() => {
    if (pageNo > totalPages) setPageNo(totalPages);
  }, [totalPages, pageNo]);

  const handleSearch = () => {
    setAppliedFilter(filterInput);
    setPageNo(1);
  };

  const handleClearFilter = () => {
    setFilterInput(EMPTY_FILTER);
    setAppliedFilter(EMPTY_FILTER);
    setPageNo(1);
  };

  const handlePageInput = (e) => {
    const value = Number(e.target.value);
    if (!value) return;
    setPageNo(Math.min(Math.max(1, value), totalPages));
  };

  /* ---------------- row actions ---------------- */

  const handleAddRow = () => {
    if (isAdding) {
      error("Please save the current new entry before adding another.");
      return;
    }
    setIsAdding(true);
    setPageNo(1); // new row is added at top, so go to first page
    setDataSource([
      {
        id: Date.now(),
        category: "Product",
        subCategory: "L1",
        description: "",
        isEditing: true,
        isNew: true
      },
      ...dataSource,
    ]);
  };

  const handleChange = (rowId, field, value) => {
    setDataSource((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;

        if (field === "category") {
          const validOptions =
            value === "Service" ? serviceSubCategoryOptions : productSubCategoryOptions;
          return { ...row, category: value, subCategory: validOptions[0].value };
        }
        return { ...row, [field]: value };
      })
    );
  };

  const handleSave = (row) => {
    if (!row.category || !row.subCategory || !row.description.trim()) {
      error("All fields are required.");
      return;
    }

    const request = {
      ProductCrudId: row.isNew ? null : row.id,
      CategoryId: categoryMap[row.category],
      SubCategoryId: subCategoryMap[row.subCategory],
      Description: row.description
    };

    if (row.isNew) {
      addCRUDCategory(request);
    } else {
      editCRUDCategory(request);
      setDataSource((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, isEditing: false, isNew: false } : item))
      );
    }
  };

  const handleCancel = (row) => {
    if (row.isNew) {
      setIsAdding(false);
      setDataSource((prev) => prev.filter((item) => item.id !== row.id));
    } else {
      // restore only this row (keeps formatted data and other rows intact)
      setDataSource((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...(item.originalRow || item), isEditing: false } : item
        )
      );
    }
  };

  const handleEdit = (id) => {
    setDataSource((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, isEditing: true, originalRow: { ...row } } : row
      )
    );
  };

  const handleDelete = (row) => {
    confirm("Delete", "Are you sure you want to delete", "Yes", "Cancel").then((id) => {
      if (id) deleteCRUDCategoryById(Number(row?.id));
    });
  };

  /* ---------------- render ---------------- */

  return (
    <div className="crud-category-list">
      {/* Filters */}
      <div className="category-filter-bar">
        <div className="filter-field">
          <label>Channel</label>
          <div className="filter-select">
            <Select
              placeholder="Select"
              isClearable
              styles={filterSelectStyles}
              value={categoryOptions.find((o) => o.value === filterInput.channel) || null}
              onChange={(opt) =>
                setFilterInput((prev) => ({ ...prev, channel: opt ? opt.value : null }))
              }
              options={categoryOptions}
            />
          </div>
        </div>

        <div className="filter-field">
          <label>Description</label>
          <input
            type="text"
            placeholder="Search by description"
            value={filterInput.description}
            onChange={(e) =>
              setFilterInput((prev) => ({ ...prev, description: e.target.value }))
            }
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </div>

        <div className="filter-actions">
          <Buttons buttonText="Search" buttonTypeClassName="search-btn" onClick={handleSearch} />
          <Buttons buttonText="Clear" buttonTypeClassName="btn btn-secondary" onClick={handleClearFilter} />
        </div>

        <div className="filter-add">
          <Buttons buttonText="+ Add New" buttonTypeClassName="add-btn" onClick={handleAddRow} />
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Channel</th>
              <th>Sub Category</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>

          {isCrudCategoryLoading ? (
            <tbody>
              <tr>
                <td colSpan={4}>
                  <div className="flex justify-center items-center min-h-[200px]">
                    <DataLoader />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : pagedData.length > 0 ? (
            <tbody>
              {pagedData.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.isEditing ? (
                      <Select
                        value={categoryOptions.find((opt) => opt.value === row.category)}
                        onChange={(o) => handleChange(row.id, "category", o.value)}
                        options={categoryOptions}
                      />
                    ) : (
                      row.category
                    )}
                  </td>

                  <td>
                    {row.isEditing ? (
                      <Select
                        value={subCategoryOptions.find((opt) => opt.value === row.subCategory)}
                        onChange={(o) => handleChange(row.id, "subCategory", o.value)}
                        options={row.category === "Service" ? serviceSubCategoryOptions : productSubCategoryOptions}
                      />
                    ) : (
                      row.subCategory
                    )}
                  </td>

                  <td>
                    {row.isEditing ? (
                      <input
                        value={row.description}
                        onChange={(e) => handleChange(row.id, "description", e.target.value)}
                      />
                    ) : (
                      row.description
                    )}
                  </td>

                  <td>
                    {row.isEditing ? (
                      <>
                        <button className="link-btn" onClick={() => handleSave(row)}>Save</button>
                        <button className="delete-btn" onClick={() => handleCancel(row)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="link-btn" onClick={() => handleEdit(row.id)}>Edit</button>
                        <button className="delete-btn" onClick={() => handleDelete(row)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan={4}>
                  <NoRecordFound />
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      {/* Pagination */}
      {!isCrudCategoryLoading && totalCount > 0 && (
        <div className="custom-pagination">
          <button
            className="page-btn"
            disabled={pageNo <= 1}
            onClick={() => setPageNo((p) => p - 1)}
          >
            Previous
          </button>

          <div className="page-info">
            <span>Page</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={pageNo}
              onChange={handlePageInput}
            />
            <span>of {totalPages}</span>
          </div>

          <button
            className="page-btn"
            disabled={pageNo >= totalPages}
            onClick={() => setPageNo((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default CRUDCategoryList;